import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import TripStepperTimeline from "./components/TripStepperTimeline";
import ActiveRidePage from "./ActiveRidePage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faShop,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import { CircleCheck, ArrowRight, Headphones } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import { decryptData } from "../utils/storageCrypto";
import { theme } from "../assets/theme/theme";
import SwipeButton from "./components/SwipeButton";
import HelpSupportModal from "./components/HelpSupport";

const Rides = () => {
  const [mockRides, setMockRides] = useState([]);
  const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mobile_number = queryParams.get("mobile");
  const { state } = useLocation();
  const isBrickbucket = state?.isBrickbucket || false;
  const { notifications, setNotifications } = useNotification();
  const [showSupportModal, setShowSupportModal] = useState(false);

  const [activeRide, setActiveRide] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [isTripCompleteLocal, setIsTripCompleteLocal] = useState(false);

  // Success Acknowledgement State
  const [acceptedTripData, setAcceptedTripData] = useState(null);

  useEffect(() => {
    if (mobile_number) {
      loadActiveRide(mobile_number);
    }
    requestLiveLocation();
  }, [mobile_number]);

  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return parseFloat((R * c).toFixed(1));
  };

  const requestLiveLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Geolocation error:", error.message);
          setCurrentLocation({ latitude: 16.5, longitude: 80.65 });
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 0 }
      );
    } else {
      setCurrentLocation({ latitude: 16.5, longitude: 80.65 });
    }
  };

  const calculatePickupDistance = (ride) => {
    if (!currentLocation || !ride?.route?.pickups?.length) return "N/A";
    const firstPickup = ride.route.pickups.find(
      (stop) => Number(stop.stopSequence) === 1
    );
    if (!firstPickup || !firstPickup.latitude || !firstPickup.longitude) {
      return "N/A";
    }
    const distance = haversineDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      firstPickup.latitude,
      firstPickup.longitude
    );
    return `${distance} km`;
  };

const loadActiveRide = async (mobile) => {
    console.log("Loading active ride for mobile:", mobile);
    const active = await fetchActiveRide(mobile);
    
    // Check if the backend returned a ride that is already completed
    const isCompleted = active?.status?.toLowerCase() === 'completed' || 
      (active?.steps?.length > 0 && active.steps.every(s => s.status === 'COMPLETED'));

    // Only set as active if it actually exists AND is not completed
    if (active && !isCompleted) {
      console.log("Active ride fetched:", active);
      setActiveRide(active);
      setIsTripCompleteLocal(false);
    } else {
      console.log("No active ride found (or old ride completed). Fetching new rides for:", mobile);
      setActiveRide(null);
      setIsTripCompleteLocal(false);
      if (isBrickbucket) {
        fetchRides(mobile);
      }
    }
  };

  const fetchActiveRide = async (mobile) => {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);
    try {
      if (!mobile) throw new Error("Mobile number is required");

      const response = await fetch(
        `${URL}delivery-partner/get-active-ride?mobile=${mobile}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
        }
      );

      const result = await response.json();
     
      console.log(result)
      
      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch active ride");
      }
      return result.data;
    } catch (error) {
      console.error("Error fetching active ride:", error.message);
      return null;
    }
  };

  const fetchRides = async (mobile) => {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);
    try {
      if (!mobile) throw new Error("Mobile number is required");

      const response = await fetch(
        `${URL}delivery-partner/get-rides?mobile=${mobile}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
        }
      );
      const data = await response.json();
      console.log("Fetched rides data:", data);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setMockRides(data.data || []);
    } catch (error) {
      console.error("Failed to load rides", error);
    }
  };

  const acceptRide = async (shipmentId) => {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);
    try {
      if (!mobile_number) {
        throw new Error("Mobile number is required to accept ride");
      }

      const response = await fetch(`${URL}delivery-partner/accept-ride`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify({ tripId:shipmentId, mobile: mobile_number }),
      });

      if (response.status === 409) {
        const errorData = await response.json();
        const conflictError = new Error(
          errorData.message || "This trip is no longer available."
        );
        conflictError.status = 409;
        throw conflictError;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to accept ride");
      }

      return await response.json();
    } catch (error) {
      console.error("Accept ride failed:", error.message);
      throw error;
    }
  };

  const handleAcceptRide = async (ride) => {
    console.log("Attempting to accept ride:", ride);
    try {
      await acceptRide(ride._id);
      // Trigger confirmation popup with accepted ride context
      setAcceptedTripData(ride);
    } catch (error) {
      if (error.status === 409) {
        alert(error.message || "Trip already taken. Refreshing trips...");
        if (isBrickbucket) {
          await fetchRides(mobile_number);
        }
      } else {
        alert(error.message);
      }
      throw error;
    }
  };

  const handleProceedToActiveRide = async () => {
    setAcceptedTripData(null);
    await loadActiveRide(mobile_number);
  };

  const handleRefreshDistance = () => {
    setLoadingDistance(true);
    requestLiveLocation();
    setTimeout(() => setLoadingDistance(false), 500);
  };

  const handleBecomeBrickbucket = () => {
    console.log("Redirecting to become Brickbucket partner");
  };

  const handleFinalStepComplete = () => {
    setIsTripCompleteLocal(true);
  };

const stepsList = activeRide?.steps || [];
  const completedCount = isTripCompleteLocal
    ? stepsList.length
    : stepsList.filter((s) => {
        const st = s.status ? s.status.toUpperCase() : "";
        return st === "COMPLETED" || st === "SKIPPED" || (s.type === "PICKUP" && (st === "IN_PROGRESS" || st === "IN PROGESS"));
      }).length;

  return (
    <div
      className="min-h-screen relative"
      style={{ backgroundColor: theme.colors.offWhite }}
    >
      {/* ----------------- TRIP ACCEPTED MODAL ----------------- */}
      {acceptedTripData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-2xl border flex flex-col items-center space-y-4 animate-scaleUp"
            style={{
              borderColor: theme.colors.grey002,
              borderRadius: `${theme.radius[24]}px`,
            }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center border shadow-sm"
              style={{
                backgroundColor: `${theme.colors.success}15`,
                borderColor: `${theme.colors.success}30`,
              }}
            >
              <CircleCheck
                className="w-9 h-9"
                style={{ color: theme.colors.success }}
              />
            </div>

            <div className="space-y-1">
              <h3
                className="text-xl font-bold leading-tight"
                style={{
                  fontFamily: "BarlowCondensed-Bold",
                  color: theme.colors.bitumen,
                }}
              >
                Trip Accepted!
              </h3>
              <p
                className="text-xs"
                style={{
                  fontFamily: "DMSans-Regular",
                  color: theme.colors.concrete,
                }}
              >
                Trip assigned successfully. Proceed to the first pickup location.
              </p>
            </div>

            <div
              className="w-full rounded-2xl p-3 border grid grid-cols-2 gap-2 text-left"
              style={{
                backgroundColor: theme.colors.grey001,
                borderColor: theme.colors.grey005,
              }}
            >
              <div>
                <p className="text-[10px] uppercase font-bold text-gray-400">Total Stops</p>
                <p className="text-sm font-bold text-black">
                  {(acceptedTripData.route?.pickups?.length || 0) +
                    (acceptedTripData.route?.dropoffs?.length || 0)}{" "}
                  Stops
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-gray-400">Total Fare</p>
                <p className="text-sm font-bold text-green-600">
                  {acceptedTripData.earnings || "₹0"}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleProceedToActiveRide}
              className="w-full py-3.5 text-white font-bold transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                borderRadius: `${theme.radius[16]}px`,
                backgroundColor: theme.colors.brand,
                boxShadow: `0 4px 14px ${theme.colors.brand}40`,
              }}
            >
              <span>Start Trip Route</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

  {/* ----------------- 1. PINNED STICKY HEADER & STEPPER ----------------- */}
{activeRide ? (
  <header
    className="sticky top-0 z-40 border-b shadow-xs pb-1"
    style={{
      backgroundColor: theme.colors.pureWhite,
      borderColor: theme.colors.grey002,
    }}
  >
    {/* Top Bar: Left Title & Right Help Button */}
    <div
      className="pb-2 mb-1 border-b max-w-md mx-auto flex items-center justify-between px-4 pt-3"
      style={{ borderColor: theme.colors.grey005 }}
    >
      <div className="min-w-0 pr-2">
        <h1
          className="text-base font-bold leading-tight tracking-wide truncate"
          style={{
            fontFamily: "BarlowCondensed-Bold",
            color: theme.colors.bitumen,
          }}
        >
          {(() => {
            const pCount = (stepsList || []).filter((s) => s.type === "PICKUP").length;
            const dCount = (stepsList || []).filter((s) => s.type === "DROPOFF").length;

            const summaryParts = [];
            if (pCount > 0) {
              summaryParts.push(`${pCount} ${pCount === 1 ? "Pickup" : "Pickups"}`);
            }
            if (dCount > 0) {
              summaryParts.push(`${dCount} ${dCount === 1 ? "Dropoff" : "Dropoffs"}`);
            }

            return `Trip — ${summaryParts.length > 0 ? summaryParts.join(" and ") : "No Stops"}`;
          })()}
        </h1>
        <p
          className="text-[11px] mt-0.5"
          style={{
            fontFamily: "DMSans-Regular",
            color: theme.colors.concrete,
          }}
        >
          {completedCount} of {stepsList.length} stops completed
        </p>
      </div>

      {/* Pinned Clean Help Button */}
      <div className="shrink-0">
        <button
          type="button"
          onClick={() => setShowSupportModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-150 active:scale-95 cursor-pointer shadow-2xs hover:bg-gray-50"
          style={{
            backgroundColor: theme.colors.offWhite,
            borderColor: theme.colors.grey002,
          }}
        >
          <Headphones className="w-3.5 h-3.5 text-black" />
          <span
            className="text-xs font-bold"
            style={{
              fontFamily: "DMSans-SemiBold",
              color: theme.colors.bitumen,
            }}
          >
            Help
          </span>
        </button>
      </div>
    </div>

    {/* Stepper Timeline */}
    <TripStepperTimeline
      steps={stepsList}
      isTripComplete={isTripCompleteLocal}
    />
  </header>
) : (
  <div className="sticky top-0 z-40 bg-white shadow-xs">
    <Navbar />
  </div>
)}

      {/* ----------------- 2. CONTENT AREA ----------------- */}
      <div className="px-4 py-3">
        <div className="max-w-md mx-auto space-y-3">
          {activeRide ? (
            <ActiveRidePage
              activeRide={activeRide}
              GetRideDetails={() => loadActiveRide(mobile_number)}
              mobile={mobile_number}
              onGetNewRides={() => loadActiveRide(mobile_number)} // Prevents passing the click event
              onFinalStepComplete={handleFinalStepComplete}
            />
          ) : isBrickbucket ? (
            mockRides.length > 0 ? (
              mockRides.map((ride) => (
                <div
                  key={ride._id || ride.id}
                  className="border transition-all shadow-xs overflow-hidden"
                  style={{
                    backgroundColor: theme.colors.pureWhite,
                    borderRadius: `${theme.radius[24]}px`,
                    borderColor: theme.colors.grey002,
                  }}
                >
                  <div className="p-4 space-y-3.5">
                    <div
                      className="flex items-center justify-between pb-3 border-b"
                      style={{ borderColor: theme.colors.grey005 }}
                    >
                      <div>
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.concrete,
                          }}
                        >
                          Trip Distance
                        </p>
                        <p
                          className="text-base font-extrabold leading-tight mt-0.5"
                          style={{
                            fontFamily: "BarlowCondensed-Bold",
                            color: theme.colors.bitumen,
                          }}
                        >
                          {ride.route?.totalDistance || ride.rideDistance || "0 km"}
                        </p>
                      </div>

                      <div className="text-right">
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.concrete,
                          }}
                        >
                          Distance to 1st Pickup
                        </p>
                        <button
                          onClick={handleRefreshDistance}
                          disabled={loadingDistance}
                          title="Tap to refresh distance"
                          className="inline-flex items-center gap-1.5 mt-0.5 transition-opacity disabled:opacity-50 cursor-pointer active:scale-95"
                          style={{
                            fontFamily: "DMMono-Medium",
                            fontSize: "13px",
                            color: theme.colors.primaryRed,
                          }}
                        >
                          <span>
                            {loadingDistance
                              ? "Updating..."
                              : calculatePickupDistance(ride)}
                          </span>
                          <FontAwesomeIcon
                            icon={faRotateRight}
                            className={`text-[10px] ${
                              loadingDistance ? "animate-spin" : ""
                            }`}
                            style={{ color: theme.colors.primaryRed }}
                          />
                        </button>
                      </div>
                    </div>

                    <div
                      className="rounded-2xl p-3.5 space-y-3 border"
                      style={{
                        backgroundColor: theme.colors.grey001,
                        borderColor: theme.colors.grey005,
                      }}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <FontAwesomeIcon
                              icon={faShop}
                              className="text-xs"
                              style={{ color: theme.colors.charcoal }}
                            />
                            <p
                              className="text-[11px] font-bold uppercase tracking-wider"
                              style={{
                                fontFamily: "BarlowCondensed-SemiBold",
                                color: theme.colors.bitumen,
                              }}
                            >
                              Pickups ({ride.route?.pickups?.length || 0})
                            </p>
                          </div>
                          <ul className="space-y-2">
                            {ride.route?.pickups?.map((p, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-1.5"
                              >
                                <span
                                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                                  style={{
                                    backgroundColor: theme.colors.bitumen,
                                  }}
                                />
                                <div className="min-w-0">
                                  <p
                                    className="text-xs font-semibold leading-tight line-clamp-2"
                                    style={{
                                      fontFamily: "DMSans-Medium",
                                      color: theme.colors.bitumen,
                                    }}
                                  >
                                    {p.name}
                                  </p>
                                  {p.area && (
                                    <p
                                      className="text-[10px] truncate mt-0.5"
                                      style={{
                                        fontFamily: "DMSans-Regular",
                                        color: theme.colors.concrete,
                                      }}
                                    >
                                      {p.area}
                                    </p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <FontAwesomeIcon
                              icon={faTruck}
                              className="text-xs"
                              style={{ color: theme.colors.primaryRed }}
                            />
                            <p
                              className="text-[11px] font-bold uppercase tracking-wider"
                              style={{
                                fontFamily: "BarlowCondensed-SemiBold",
                                color: theme.colors.bitumen,
                              }}
                            >
                              Drop-offs ({ride.route?.dropoffs?.length || 0})
                            </p>
                          </div>
                          <ul className="space-y-2">
                            {ride.route?.dropoffs?.map((d, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-1.5"
                              >
                                <span
                                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                                  style={{
                                    backgroundColor: theme.colors.primaryRed,
                                  }}
                                />
                                <div className="min-w-0">
                                  <p
                                    className="text-xs font-semibold leading-tight line-clamp-2"
                                    style={{
                                      fontFamily: "DMSans-Medium",
                                      color: theme.colors.bitumen,
                                    }}
                                  >
                                    {d.name}
                                  </p>
                                  {d.area && (
                                    <p
                                      className="text-[10px] truncate mt-0.5"
                                      style={{
                                        fontFamily: "DMSans-Regular",
                                        color: theme.colors.concrete,
                                      }}
                                    >
                                      {d.area}
                                    </p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div
                        className="rounded-xl py-2 px-3 text-center border flex flex-col justify-center items-center"
                        style={{
                          backgroundColor: theme.colors.grey001,
                          borderColor: theme.colors.grey005,
                        }}
                      >
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.concrete,
                          }}
                        >
                          Total Items
                        </p>
                        <p
                          className="text-base font-bold leading-tight mt-0.5"
                          style={{
                            fontFamily: "DMMono-Medium",
                            color: theme.colors.bitumen,
                          }}
                        >
                          {ride.items || 1}
                        </p>
                      </div>

                      <div
                        className="rounded-xl py-2 px-3 text-center border flex flex-col justify-center items-center"
                        style={{
                          backgroundColor: `${theme.colors.success}10`,
                          borderColor: `${theme.colors.success}30`,
                        }}
                      >
                        <p
                          className="text-[10px] uppercase font-bold tracking-wider"
                          style={{
                            fontFamily: "DMSans-Medium",
                            color: theme.colors.success,
                          }}
                        >
                          Trip Fare
                        </p>
                        <p
                          className="text-lg font-black leading-tight mt-0.5"
                          style={{
                            fontFamily: "DMMono-Medium",
                            color: theme.colors.success,
                          }}
                        >
                          {ride.earnings || "₹0"}
                        </p>
                      </div>
                    </div>

                    <SwipeButton
                      text="Swipe to Accept Trip"
                      onSwipeSuccess={() => handleAcceptRide(ride)}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div
                className="border p-6 text-center shadow-xs"
                style={{
                  backgroundColor: theme.colors.pureWhite,
                  borderRadius: `${theme.radius[24]}px`,
                  borderColor: theme.colors.grey002,
                }}
              >
                <p
                  className="text-base font-bold"
                  style={{
                    fontFamily: "BarlowCondensed-Bold",
                    color: theme.colors.bitumen,
                  }}
                >
                  No trips available now
                </p>
                <p
                  className="text-xs mt-1"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.concrete,
                  }}
                >
                  Please wait for new trips to be assigned.
                </p>
              </div>
            )
          ) : (
            <div
              className="border p-6 text-center space-y-4 shadow-xs"
              style={{
                backgroundColor: theme.colors.pureWhite,
                borderRadius: `${theme.radius[24]}px`,
                borderColor: theme.colors.grey002,
              }}
            >
              <div className="space-y-1">
                <p
                  className="text-lg font-bold"
                  style={{
                    fontFamily: "BarlowCondensed-Bold",
                    color: theme.colors.bitumen,
                  }}
                >
                  Become a Brickbucket Delivery Partner
                </p>
                <p
                  className="text-xs"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.concrete,
                  }}
                >
                  As a supplier delivery partner, you don't have access to
                  trips. Would you like to become a Brickbucket delivery partner
                  to start accepting trips?
                </p>
              </div>
              <button
                type="button"
                onClick={handleBecomeBrickbucket}
                className="w-full py-3.5 text-white font-bold transition-all shadow-md active:scale-[0.99] cursor-pointer"
                style={{
                  fontFamily: "DMSans-Medium",
                  borderRadius: `${theme.radius[16]}px`,
                  backgroundColor: theme.colors.brand,
                  boxShadow: `0 4px 14px ${theme.colors.brand}40`,
                }}
              >
                Become Brickbucket Partner
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Modal Triggered from Header */}
<HelpSupportModal
  isOpen={showSupportModal}
  onClose={() => setShowSupportModal(false)}
  activeRide={activeRide}
  currentStep={stepsList.find((s) => s.status !== "COMPLETED")}
/>
    </div>
  );
};

export default Rides;